import { useEffect, useRef } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CreateEventBody, PromoteAttendeeBody } from '@ni/shared';
import type { EventAttendeesParams, EventListParams } from './client';
import { useApi, useOrg } from './app-context';
import { PAGE_POLL_MS, qk } from './keys';

/**
 * Events' reads and writes (docs/notes/2026-09-23-event-graph-design.md §3a).
 * One key prefix, so every write moves the list and the page.
 */
export function useEvents(params: EventListParams) {
  const api = useApi();
  const org = useOrg();
  return useInfiniteQuery({
    queryKey: qk.events(org.id, params),
    queryFn: ({ pageParam }) =>
      api.listEvents(pageParam === null ? params : { ...params, cursor: pageParam }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor,
    refetchOnWindowFocus: false,
  });
}

export function useEvent(entityId: string) {
  const api = useApi();
  const org = useOrg();
  return useQuery({
    queryKey: qk.event(org.id, entityId),
    queryFn: () => api.getEvent(entityId),
    refetchInterval: PAGE_POLL_MS,
  });
}

function useInvalidateEvents() {
  const queryClient = useQueryClient();
  const org = useOrg();
  return () => {
    void queryClient.invalidateQueries({ queryKey: qk.eventsPrefix(org.id) });
    void queryClient.invalidateQueries({ queryKey: qk.knowledgePrefix(org.id) });
  };
}

export function useCreateEvent() {
  const api = useApi();
  const queryClient = useQueryClient();
  const org = useOrg();
  const invalidate = useInvalidateEvents();
  return useMutation({
    mutationFn: (body: CreateEventBody) => api.createEvent(body),
    onSuccess: (record) => {
      queryClient.setQueryData(qk.event(org.id, record.subject.entity_id), record);
    },
    onSettled: invalidate,
  });
}

export function useUntrackEvent(entityId: string) {
  const api = useApi();
  const invalidate = useInvalidateEvents();
  return useMutation({
    mutationFn: () => api.untrackEvent(entityId),
    onSettled: invalidate,
  });
}

/** Start the event's research; the record lists the run, so the record is read again. */
export function useCreateEventRun(entityId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  const org = useOrg();
  return useMutation({
    /* `expectedCreditUnits`: the price the button showed, on an enrolled account. */
    mutationFn: (expectedCreditUnits?: number | null) =>
      api.createRun(entityId, {
        kind: 'event_research',
        ...(expectedCreditUnits === undefined
          ? {}
          : {
              admission_key: crypto.randomUUID(),
              expected_credit_units: expectedCreditUnits ?? 0,
            }),
      }),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: qk.event(org.id, entityId) });
    },
  });
}

/** The Attendees tab, paged; polled so a finishing import or scan shows up. */
export function useEventAttendees(entityId: string, params: EventAttendeesParams) {
  const api = useApi();
  const org = useOrg();
  return useInfiniteQuery({
    queryKey: qk.eventAttendees(org.id, entityId, params),
    queryFn: ({ pageParam }) =>
      api.getEventAttendees(
        entityId,
        pageParam === null ? params : { ...params, cursor: pageParam },
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor,
    refetchInterval: PAGE_POLL_MS,
  });
}

/**
 * Start the attendee scan for one analysis, rating only its factors; the
 * Attendees read carries the run to follow.
 */
export function useStartEventScan(entityId: string, analysis: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  const org = useOrg();
  return useMutation({
    /** `input`: what the office entered for the analysis's parameterized factors; `{}` for none.
     * `reportInputs`: the fields of the report the scan writes when it completes.
     * `expectedCreditUnits`: the most the button showed, on an enrolled account. */
    mutationFn: ({
      input = {},
      reportInputs = {},
      expectedCreditUnits,
    }: {
      input?: Record<string, string>;
      reportInputs?: Record<string, string>;
      expectedCreditUnits?: number | null;
    }) =>
      api.startEventScan(entityId, {
        analysis,
        input,
        report_inputs: reportInputs,
        ...(expectedCreditUnits === undefined
          ? {}
          : {
              admission_key: crypto.randomUUID(),
              expected_credit_units: expectedCreditUnits ?? 0,
            }),
      }),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: qk.eventsPrefix(org.id) });
      void queryClient.invalidateQueries({ queryKey: qk.credits(org.id) });
    },
  });
}

/**
 * When the event's polled run changes state, read the event again: a finished
 * scan or research run has moved the attendees or the summary, which the page
 * otherwise polls only once a minute.
 */
export function useEventRunStateInvalidation(run: { id: string; state: string } | undefined) {
  const queryClient = useQueryClient();
  const org = useOrg();
  const seen = useRef<string | null>(null);
  useEffect(() => {
    if (!run) return;
    const key = `${run.id}:${run.state}`;
    if (seen.current !== null && seen.current !== key) {
      void queryClient.invalidateQueries({ queryKey: qk.eventsPrefix(org.id) });
      // A completed scan has started its analysis's report.
      void queryClient.invalidateQueries({ queryKey: qk.reports(org.id) });
      /* The Overview's knowledge read stops its minute tick when no run is going,
         so the run landing is what brings it up to date. */
      void queryClient.invalidateQueries({ queryKey: qk.knowledgePrefix(org.id) });
    }
    seen.current = key;
  }, [run?.id, run?.state, queryClient, org.id]);
}

export function useEventAttendee(entityId: string, nodeId: string) {
  const api = useApi();
  const org = useOrg();
  return useQuery({
    queryKey: qk.eventAttendee(org.id, entityId, nodeId),
    queryFn: () => api.getEventAttendee(entityId, nodeId),
  });
}

/**
 * Add an attendee to the records, pick which reading is them for this event
 * (or take the pick back), or say none of the readings is them.
 */
export function useAttendeeWrites(entityId: string, nodeId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  const org = useOrg();
  const settle = {
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: qk.eventsPrefix(org.id) });
      void queryClient.invalidateQueries({ queryKey: qk.knowledgePrefix(org.id) });
    },
  };
  return {
    promote: useMutation({
      mutationFn: (body: PromoteAttendeeBody) => api.promoteAttendee(entityId, nodeId, body),
      ...settle,
    }),
    dismiss: useMutation({ mutationFn: () => api.dismissAttendee(entityId, nodeId), ...settle }),
    pick: useMutation({
      mutationFn: (candidateId: string | null) =>
        candidateId
          ? api.pickAttendeeReading(entityId, nodeId, { candidate_id: candidateId })
          : api.unpickAttendeeReading(entityId, nodeId),
      ...settle,
    }),
  };
}

/** Write the report a scan held while attendees were unmatched. */
export function useWriteAnalysisReport(entityId: string, analysis: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  const org = useOrg();
  return useMutation({
    mutationFn: () => api.writeAnalysisReport(entityId, analysis),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: qk.eventsPrefix(org.id) });
      void queryClient.invalidateQueries({ queryKey: qk.reportsPrefix(org.id) });
    },
  });
}

/** Delete the attendee list after the event; records already added stay. */
export function useDeleteEventAttendees(entityId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  const org = useOrg();
  return useMutation({
    mutationFn: () => api.deleteEventAttendees(entityId),
    onSettled: () => void queryClient.invalidateQueries({ queryKey: qk.eventsPrefix(org.id) }),
  });
}

/** Add people to the event by hand: each recorded as attending, and listed. */
export function useAddEventPeople(entityId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  const org = useOrg();
  return useMutation({
    mutationFn: (entityIds: string[]) => api.addEventPeople(entityId, { entity_ids: entityIds }),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: qk.eventsPrefix(org.id) });
      void queryClient.invalidateQueries({ queryKey: qk.knowledgePrefix(org.id) });
    },
  });
}

/**
 * **Delete the event wholesale** through the subject delete: the event, its
 * attendee list, and every attendance, host and sponsor tie to it. The people
 * and organizations stay. Back to the Events list after.
 */
export function useDeleteEvent(entityId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  const org = useOrg();
  return useMutation({
    mutationFn: () => api.deleteSubject(entityId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.eventsPrefix(org.id) });
      void queryClient.invalidateQueries({ queryKey: qk.knowledgePrefix(org.id) });
    },
  });
}
